import { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { SectionCard } from '@/components/ui';
import { useTranslation } from '@/lib/i18n';
import type { LegacyPgInfo } from '../lib/refundFlags';

interface LegacyPgInfoCardProps {
  legacyPg: LegacyPgInfo;
}

const CopyButton = ({ value, label }: { value: string; label: string }) => {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      className="ml-2 inline-flex items-center rounded p-1"
      style={{ color: 'var(--content-muted)' }}
      aria-label={label}
      onClick={() => {
        void navigator.clipboard?.writeText(value).then(() => {
          setCopied(true);
          window.setTimeout(() => setCopied(false), 1500);
        });
      }}
    >
      {copied ? <Check size={14} aria-hidden="true" /> : <Copy size={14} aria-hidden="true" />}
    </button>
  );
};

/**
 * "원 PG 정보(고도몰)" — 이관 결제의 고도몰 PG 정보(be-payment c5f5934 legacyPg). 용어는 고도몰 관리자와 같다.
 * 운영자가 원 PG 관리자 화면(KCP·카카오페이 등)에서 거래를 찾아 취소할 때 쓰도록 거래·승인 번호에 복사 버튼을 둔다.
 * 승인 일시는 KST 원값 문자열이라 변환하지 않고 그대로 보인다.
 */
export const LegacyPgInfoCard = ({ legacyPg }: LegacyPgInfoCardProps) => {
  const { t } = useTranslation('payments');
  const dash = '—';
  const row = (label: string, value: string | null | undefined, copy?: boolean, testId?: string) => (
    <>
      <dt className="text-[color:var(--content-muted)]">{label}</dt>
      <dd className="flex items-center text-[color:var(--content-strong)]" data-testid={testId}>
        <span className="tabular-nums break-all">{value?.trim() ? value : dash}</span>
        {copy && value?.trim() ? (
          <CopyButton value={value.trim()} label={t('payments.legacyPg.copy', { label, defaultValue: '{{label}} 복사' })} />
        ) : null}
      </dd>
    </>
  );

  return (
    <SectionCard title={t('payments.legacyPg.title', '원 PG 정보(고도몰)')} data-testid="legacy-pg-info">
      {legacyPg.pgCancelled ? (
        <p
          role="alert"
          className="mb-3 rounded px-2 py-1.5 text-[12px]"
          style={{ background: 'var(--status-danger-bg)', color: 'var(--status-danger-fg)' }}
          data-testid="legacy-pg-cancelled"
        >
          {t('payments.legacyPg.cancelled', '고도몰에서 이미 PG 취소된 결제입니다. 중복 환불하지 마세요.')}
        </p>
      ) : (
        <p className="mb-3 text-[12px] text-[color:var(--content-muted)]" data-testid="legacy-pg-guide">
          {t('payments.legacyPg.guide', {
            pg: legacyPg.pgLabel ?? legacyPg.pgName ?? 'PG',
            defaultValue: '고도몰 이관 결제(원 PG: {{pg}}) — 원 PG 관리자에서 취소 후 [수기 환불 기록]',
          })}
        </p>
      )}
      <dl className="grid grid-cols-[110px_1fr] gap-x-3 gap-y-1.5 text-sm">
        {row(t('payments.legacyPg.pg', '원 PG'), legacyPg.pgLabel ?? legacyPg.pgName)}
        {row(t('payments.legacyPg.settleKind', '결제수단'), legacyPg.settleKindLabel ?? legacyPg.settleKind)}
        {row(t('payments.legacyPg.tid', 'PG 거래 번호'), legacyPg.pgTid, true, 'legacy-pg-tid')}
        {row(t('payments.legacyPg.appNo', 'PG 승인 번호'), legacyPg.pgAppNo, true, 'legacy-pg-appno')}
        {row(t('payments.legacyPg.appDt', '승인 일시'), legacyPg.pgAppDt)}
      </dl>
    </SectionCard>
  );
};
